// Shared with the AI gateway by scripts/sync-shared.js.
const question = (id, label, hint, required = false) => ({ id, label, hint, required });
export const TICKET_TEMPLATES = {
  bug: {
    label: 'Bug', description: 'Make a problem reproducible and define the fix.',
    steps: [
      { title: 'Describe the problem', questions: [
        question('problem', 'What is going wrong?', 'Describe the failure and who encounters it.', true),
        question('environment', 'Where does it happen?', 'Include the app version, device, browser, or environment.'),
        question('impact', 'Who is affected, and how often?', 'Describe the impact, frequency, and any workaround.'),
      ] },
      { title: 'Reproduce it', questions: [
        question('reproduction', 'How can someone reproduce it?', 'List the steps in order, including any prerequisites.', true),
        question('expected', 'What should happen?', 'Describe the expected behavior.', true),
        question('actual', 'What happens instead?', 'Include error messages, observations, or links to evidence.', true),
      ] },
      { title: 'Define the fix', questions: [
        question('acceptance', 'How will we know it is fixed?', 'List observable checks and regression scenarios.'),
        question('context', 'Anything else the team needs?', 'Add logs, related tickets, constraints, or open questions.'),
      ] },
    ],
  },
  epic: {
    label: 'Epic', description: 'Shape a larger outcome into a clear scope of work.',
    steps: [
      { title: 'Set the outcome', questions: [
        question('problem', 'What problem are we solving?', 'Explain the unmet need and why it matters now.', true),
        question('audience', 'Who is this for?', 'Name the users and their main jobs or use cases.', true),
        question('outcome', 'What outcome should this deliver?', 'Describe the change users or the business should see.', true),
      ] },
      { title: 'Shape the scope', questions: [
        question('scope', 'What is in scope?', 'List the main capabilities or workstreams.', true),
        question('nonGoals', 'What is out of scope?', 'Set boundaries so the team knows what to leave out.'),
        question('dependencies', 'What does this depend on?', 'Name related teams, systems, decisions, or risks.'),
      ] },
      { title: 'Measure success', questions: [
        question('acceptance', 'What must be true to call this done?', 'List the acceptance criteria across the whole epic.'),
        question('metrics', 'How will we measure success?', 'Use known baselines and targets; leave unknown values open.'),
        question('rollout', 'How should it roll out?', 'Describe sequencing, milestones, and any constraints.'),
      ] },
    ],
  },
  initiative: {
    label: 'Initiative', description: 'Connect a strategic investment to measurable outcomes.',
    steps: [
      { title: 'Explain the opportunity', questions: [
        question('problem', 'What opportunity are we pursuing?', 'Describe the problem and the evidence behind it.', true),
        question('strategy', 'Which strategic objective does it support?', 'Explain the connection to a business goal or OKR.', true),
        question('audience', 'Who will benefit?', 'Describe the customers, teams, or market segment.'),
      ] },
      { title: 'Define the investment', questions: [
        question('outcome', 'What change do we expect?', 'State the outcome and your hypothesis for achieving it.', true),
        question('scope', 'Which workstreams are included?', 'Outline the major bets, boundaries, and non-goals.', true),
        question('resources', 'What resources or dependencies are needed?', 'Include ownership, capacity, constraints, and risks.'),
      ] },
      { title: 'Plan the learning', questions: [
        question('metrics', 'How will we measure progress?', 'Add known success metrics, baselines, and targets.'),
        question('validation', 'What should we validate first?', 'Identify assumptions, experiments, and decision checkpoints.'),
        question('timeline', 'What is the intended horizon?', 'Include timing and milestones if already agreed.'),
      ] },
    ],
  },
  prd: {
    label: 'PRD', description: 'Turn a product idea into requirements the team can act on.',
    steps: [
      { title: 'Establish the need', questions: [
        question('problem', 'What problem does this product solve?', 'Describe the current experience and supporting evidence.', true),
        question('audience', 'Who are the target users?', 'Describe personas, needs, and primary use cases.', true),
        question('outcome', 'What are the goals and success measures?', 'State the intended outcomes and any known metrics.', true),
      ] },
      { title: 'Specify the experience', questions: [
        question('requirements', 'What must the product do?', 'List functional requirements and key user journeys.', true),
        question('nonGoals', 'What is explicitly out of scope?', 'Define the boundaries for this release.'),
        question('quality', 'What quality constraints apply?', 'Consider accessibility, performance, privacy, and reliability.'),
      ] },
      { title: 'Prepare for delivery', questions: [
        question('acceptance', 'What are the acceptance criteria?', 'Describe testable behavior, edge cases, and failure states.'),
        question('dependencies', 'What risks and dependencies remain?', 'Include unresolved decisions and links to designs or research.'),
        question('rollout', 'How will we launch and learn?', 'Describe release stages, instrumentation, and follow-up.'),
      ] },
    ],
  },
};

export const ticketQuestions = type => TICKET_TEMPLATES[type]?.steps.flatMap(step => step.questions) || [];
export const ticketMarkdown = draft => `# ${draft.title}\n\n${draft.description}\n`;

export function ticketBrief(type, answers) {
  if (!Object.hasOwn(TICKET_TEMPLATES, type)) throw new Error('Choose a ticket type.');
  return {
    type: 'ticket_brief', id: 'current', ticket_type: type,
    answers: ticketQuestions(type).map(q => ({ id: q.id, question: q.label, answer: String(answers[q.id] || '').slice(0, 3000) })),
  };
}
